import { useMemo, useState, useEffect } from "react";

const PAGE_SIZE = 15;

/**
 * usePager: client-side pagination for admin lists.
 * Returns { page, setPage, pageItems, total, pages, from, to }
 */
export function usePager(items, pageSize = PAGE_SIZE) {
  const [page, setPage] = useState(1);
  const total = items?.length || 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  // clamp page if items shrink (e.g. after delete)
  useEffect(() => {
    if (page > pages) setPage(pages);
  }, [pages, page]);

  const pageItems = useMemo(
    () => (items || []).slice((page - 1) * pageSize, page * pageSize),
    [items, page, pageSize]
  );

  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return { page, setPage, pageItems, total, pages, from, to };
}

export default function Pager({ page, setPage, total, pages, from, to, testid = "pager" }) {
  // Hide only when the whole set fits on a single page — deduce that from `pages`,
  // not from a hard-coded threshold (usePager can be called with a custom pageSize).
  if (pages <= 1) return null;
  return (
    <div
      data-testid={testid}
      className="mt-4 flex items-center justify-between text-[0.7rem] uppercase tracking-[0.16em] text-ivory/50"
    >
      <span data-testid={`${testid}-range`}>{from}–{to} of {total}</span>
      <div className="flex items-center gap-2">
        <button
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          className="btn-outline !py-1 !px-3 !text-[0.65rem] disabled:opacity-30"
          data-testid={`${testid}-prev`}
        >
          Prev
        </button>
        <span className="font-mono text-ivory/70">{page} / {pages}</span>
        <button
          disabled={page >= pages}
          onClick={() => setPage((p) => Math.min(pages, p + 1))}
          className="btn-outline !py-1 !px-3 !text-[0.65rem] disabled:opacity-30"
          data-testid={`${testid}-next`}
        >
          Next
        </button>
      </div>
    </div>
  );
}
